/**
 * @license
 * @copyright Copyright Motili Inc., 2017 All Rights Reserved
 */

import {
    merge,
    union,
    some,
    map,
    uniq,
    concat,
    filter as _filter,
} from 'lodash';

import moment from 'moment-timezone';
import {
    hasValidSerialNumberEditCategory,
    hasValidSerialNumberEditOrderStatus,
} from 'common/services/LineItemService';
import { getFinanceUpdateLineItems } from 'common/utils/line-item-utils';
import * as ApiService from 'common/services/ApiService';
import * as OrderService from 'common/services/OrderService';
import * as FinancialService from 'common/services/FinancialService';
import * as AdminClientsService from 'admin/services/AdminClientsService';
import { findById } from 'common/services/AccountService';
import {
    hasRole,
    isMotiliAccount,
    isMotiliFinanceDept,
    isMotiliAdmin,
    isMotiliEngineeringDept,
    isMotiliApFinanceDept,
} from 'common/services/AuthService';
import * as DocumentService from 'common/services/DocumentService';
import { OrderSearchService } from 'common/services/SearchService';
import { haveItemsToReturn } from 'common/utils/order-return-utils';
import {
    ORDER_STATUS,
    ORDER_TYPE,
} from 'common/stores/redux/MetaStore/MetaStoreApi.types';
import { createContainsLoopbackFilter } from 'common/components/data-grid/DataGridUtils';

export function getOrderActivityCount(orderId) {
    return OrderService.getVendorActivityCount(orderId);
}

export function getOrderActivity(orderId, skip = 0, limit = 20) {
    const query = {
        filter: {
            include: [
                {
                    relation: 'vendorOrderActivity',
                    scope: {
                        include: ['vendorStatusLineItems'],
                        limit,
                        skip,
                        order: 'created DESC',
                    },
                },
            ],
        },
    };
    return OrderService.findById(orderId, query);
}

export function getOrder(orderId) {
    return OrderService.fetchOrderByFilterId(orderId, 'BASE')
        .then(order => {
            const promises = [];
            promises.push(
                order.contractorId &&
                    order.orderTypeId !== ORDER_TYPE.LABOR_WARRANTY
                    ? OrderService.getOrderRules(order.id)
                    : {}
            );
            promises.push(order);
            return Promise.all(promises);
        })
        .then(([rules, order]) => {
            const promises = [];
            const accountQuery = {
                filter: {
                    fields: ['id', 'firstName', 'lastName'],
                },
            };
            promises.push(
                order.orderedById
                    ? findById(order.orderedById, accountQuery)
                    : {}
            );
            promises.push(rules);
            promises.push(order);
            return Promise.all(promises);
        })
        .then(([account, rules, order]) => ({
            ...order,
            orderedBy: account,
            rules,
        }));
}

export function getOrderDetails(orderId) {
    return OrderService.fetchOrderByFilterId(orderId, 'DETAILS')
        .then(order => {
            const promises = [];
            promises.push(
                order.contractorId &&
                    order.orderTypeId !== ORDER_TYPE.LABOR_WARRANTY
                    ? OrderService.getOrderRules(order.id)
                    : {}
            );
            promises.push(order);
            return Promise.all(promises);
        })
        .then(([rules, order]) => {
            const promises = [];
            const accountQuery = {
                filter: {
                    fields: ['id', 'firstName', 'lastName'],
                },
            };
            promises.push(
                order.orderedById
                    ? findById(order.orderedById, accountQuery)
                    : {}
            );
            promises.push(rules);
            promises.push(order);
            return Promise.all(promises);
        })
        .then(([account, rules, order]) => ({
            ...order,
            orderedBy: account,
            rules,
        }));
}

export function getOrderFiles(orderId) {
    const query = {
        filter: {
            fields: ['id', 'folderId'],
            include: [
                {
                    relation: 'folder',
                    scope: {
                        fields: ['id', 'folderAttachments'],
                        include: {
                            relation: 'folderAttachments',
                            scope: {
                                fields: [
                                    'id',
                                    'folderId',
                                    'fileName',
                                    'mimeType',
                                    'url',
                                    'secureUrl',
                                    'folderAttachmentTypeId',
                                    'createdAt',
                                    'uploadedById',
                                    'uploadedByCompanyId',
                                ],
                                include: [
                                    {
                                        relation: 'uploadedBy',
                                        scope: {
                                            fields: [
                                                'id',
                                                'firstName',
                                                'lastName',
                                            ],
                                        },
                                    },
                                ],
                            },
                        },
                    },
                },
            ],
        },
    };

    return OrderService.findById(orderId, query);
}

export function getOrderHistory(orderId) {
    const query = {
        filter: {
            fields: ['id', 'jobId'],
            include: [
                {
                    relation: 'job',
                    scope: {
                        fields: ['id', 'number', 'projectId', 'uuid'],
                        include: [
                            {
                                relation: 'project',
                                scope: {
                                    fields: ['name'],
                                },
                            },
                            {
                                relation: 'notes',
                            },
                            {
                                relation: 'reasons',
                                scope: {
                                    fields: [
                                        'id',
                                        'reasonType',
                                        'reasonSubType',
                                        'reasonDetails',
                                        'documentId',
                                        'documentTypeId',
                                    ],
                                },
                            },
                            {
                                relation: 'sequence',
                                scope: {
                                    fields: ['sequenceNumber', 'documentId'],
                                },
                            },
                            {
                                relation: 'workOrders',
                                scope: {
                                    fields: [
                                        'id',
                                        'number',
                                        'statusId',
                                        'documentTypeId',
                                        'userId',
                                        'workOrderTypeId',
                                        'priorityId',
                                        'created',
                                        'propertyId',
                                        'contractorId',
                                        'contractorTeamMemberId',
                                        'comments',
                                        'tradeTypeId',
                                        'tradeSubTypeId',
                                        'events',
                                        'schedule',
                                        'checkIns',
                                        'fulfillmentDetails',
                                    ],
                                    include: [
                                        {
                                            relation: 'user',
                                            scope: {
                                                fields: [
                                                    'firstName',
                                                    'lastName',
                                                ],
                                            },
                                        },
                                        {
                                            relation: 'property',
                                            scope: {
                                                fields: ['stdAddress'],
                                            },
                                        },
                                        {
                                            relation: 'contractor',
                                            scope: {
                                                fields: [
                                                    'name',
                                                    'phoneContacts',
                                                    'addressId',
                                                ],
                                                include: [
                                                    {
                                                        relation: 'address',
                                                        scope: {
                                                            fields: 'stdAddress',
                                                        },
                                                    },
                                                ],
                                            },
                                        },
                                        {
                                            relation: 'contractorTeamMember',
                                            scope: {
                                                fields: [
                                                    'email',
                                                    'phone',
                                                    'phoneCountry',
                                                    'firstName',
                                                    'lastName',
                                                ],
                                            },
                                        },
                                        {
                                            relation: 'events',
                                            scope: {
                                                fields: ['action', 'created'],
                                            },
                                        },
                                        {
                                            relation: 'sequenceElement',
                                            scope: {
                                                fields: [
                                                    'sequenceNumber',
                                                    'nextDocumentId',
                                                    'nextDocumentTypeId',
                                                    'nextDocumentSubTypeId',
                                                ],
                                            },
                                        },
                                        { relation: 'checkInsV2' },
                                    ],
                                },
                            },
                            {
                                relation: 'quotes',
                                scope: {
                                    fields: [
                                        'id',
                                        'number',
                                        'statusId',
                                        'documentTypeId',
                                        'userId',
                                        'quoteTypeId',
                                        'priorityId',
                                        'created',
                                        'propertyId',
                                        'contractorId',
                                        'contractorTeamMemberId',
                                        'comments',
                                        'tradeTypeId',
                                        'tradeSubTypeId',
                                        'events',
                                        'fulfillmentDetails',
                                    ],
                                    include: [
                                        {
                                            relation: 'user',
                                            scope: {
                                                fields: [
                                                    'firstName',
                                                    'lastName',
                                                ],
                                            },
                                        },
                                        {
                                            relation: 'property',
                                            scope: {
                                                fields: ['stdAddress'],
                                            },
                                        },
                                        {
                                            relation: 'contractor',
                                            scope: {
                                                fields: [
                                                    'name',
                                                    'phoneContacts',
                                                    'addressId',
                                                ],
                                                include: [
                                                    {
                                                        relation: 'address',
                                                        scope: {
                                                            fields: 'stdAddress',
                                                        },
                                                    },
                                                ],
                                            },
                                        },
                                        {
                                            relation: 'contractorTeamMember',
                                            scope: {
                                                fields: [
                                                    'email',
                                                    'phone',
                                                    'phoneCountry',
                                                    'firstName',
                                                    'lastName',
                                                ],
                                            },
                                        },
                                        {
                                            relation: 'events',
                                            scope: {
                                                fields: ['action', 'created'],
                                            },
                                        },
                                        {
                                            relation: 'sequenceElement',
                                            scope: {
                                                fields: ['sequenceNumber'],
                                            },
                                        },
                                    ],
                                },
                            },
                            {
                                relation: 'orders',
                                scope: {
                                    fields: [
                                        'id',
                                        'number',
                                        'statusId',
                                        'documentTypeId',
                                        'userId',
                                        'orderTypeId',
                                        'priorityId',
                                        'created',
                                        'propertyId',
                                        'comments',
                                        'tradeTypeId',
                                        'tradeSubTypeId',
                                        'events',
                                        'fulfillmentDistributionCenterId',
                                    ],
                                    include: [
                                        {
                                            relation: 'user',
                                            scope: {
                                                fields: [
                                                    'firstName',
                                                    'lastName',
                                                ],
                                            },
                                        },
                                        {
                                            relation: 'events',
                                            scope: {
                                                fields: ['action', 'created'],
                                            },
                                        },
                                        {
                                            relation: 'sequenceElement',
                                            scope: {
                                                fields: ['sequenceNumber'],
                                            },
                                        },
                                    ],
                                },
                            },
                        ],
                    },
                },
            ],
        },
    };

    return OrderService.findById(orderId, query);
}

export function getOrders(filterQuery) {
    const filterFields = {
        filter: {
            include: [
                {
                    relation: 'vendorDistributionCenter',
                    scope: {
                        fields: ['vendorId', 'stdAddress'],
                        include: [
                            {
                                relation: 'vendor',
                                scope: {
                                    fields: ['doingBusinessAs'],
                                },
                            },
                        ],
                    },
                },
                {
                    relation: 'user',
                    scope: {
                        fields: ['firstName', 'lastName'],
                    },
                },
                {
                    relation: 'vendor',
                    scope: {
                        fields: ['name', 'doingBusinessAs'],
                    },
                },
                {
                    relation: 'property',
                    scope: {
                        fields: ['id', 'stdAddress'],
                    },
                },
            ],
            fields: [
                'id',
                'statusId',
                'tradeTypeId',
                'tradeSubTypeId',
                'priorityId',
                'documentTypeId',
                'documentSubTypeId',
                'number',
                'clientId',
                'client',
                'fulfillmentTypeId',
                'fulfillmentTo',
                'fulfillmentToAddress',
                'fulfillmentToCity',
                'fulfillmentToRegion',
                'fulfillmentToPostalCode',
                'vendorBranchName',
                'vendorBranchNumber',
                'vendorOrderNumber',
                'vendorId',
                'extReferenceNumber1',
                'extReferenceNumber2',
                'extReferenceNumber3',
                'extReferenceNumber4',
                'extReferenceNumber5',
                'extPoNumber',
                'comments',
                'created',
                'history',
                'orderedById',
                'orderedByFullName',
                'createdByFullName',
                'orderedOn',
                'closedOn',
                'fulfillmentDistributionCenterId',
                'userId',
                'jobId',
                'contractorId',
                'issueId',
                'isEcommOrder',
                'clientOrderApprovalStatus',
                'fulfillmentMethod',
                'vendorShipTrackingNumberManual',
                'vendorLocalTruckAutomated',
                'vendorVerifiedAutomated',
                'vendorLocalTruckManual',
                'propertyId',
                'payeeClientId',
                'payeeClientName',
                'priceBookClientId',
                'grandTotal',
                'hasEquipmentWarrantyAssets',
                'projectId',
            ],
        },
    };
    const query = merge({}, filterFields, { filter: filterQuery });
    return OrderSearchService.find(query);
}

export function getCancelReasons() {
    return ApiService.get(true, 'Orders/cancelReasons');
}

export function getCancelAndCloneReasons() {
    return ApiService.get(true, 'Orders/cloneReasons');
}

export function getCountOfOrders(query) {
    if (query.where) return OrderSearchService.count({ where: query.where });
    return OrderService.count(query);
}

export function getOrderLogisticsTypes() {
    return ApiService.get(true, 'Orders/logisticsTypes');
}

export function getShippingMethods() {
    return ApiService.get(true, 'Orders/shippingMethods');
}

export function updateVendorOrderShippingMethod(data) {
    return ApiService.post(
        true,
        'Orders/updateVendorOrderShippingMethod',
        data
    );
}

export async function processFilters(
    searchFilters,
    advancedFilters,
    activeQuery,
    userId
) {
    const query = merge({}, activeQuery, {
        where: {
            and: [],
        },
    });

    if (
        !Object.keys(searchFilters).length &&
        !Object.keys(advancedFilters).length
    ) {
        return query;
    }

    // Search filter section
    const filterGroups = {};
    const searchFilterProperties = {
        CLIENT: 'client',
        CREATED_BY: 'createdByFullName',
        ORDERED_BY: 'orderedByFullName',
        NUMBER: 'number',
        PO_NUMBER: 'extPoNumber',
        EXT_REFERENCE_NUMBER_1: 'extReferenceNumber1',
        EXT_REFERENCE_NUMBER_2: 'extReferenceNumber2',
        EXT_REFERENCE_NUMBER_3: 'extReferenceNumber3',
        EXT_REFERENCE_NUMBER_4: 'extReferenceNumber4',
        EXT_REFERENCE_NUMBER_5: 'extReferenceNumber5',
        VENDOR_BRANCH_NAME: 'vendorBranchName',
        VENDOR_BRANCH_NUMBER: 'vendorBranchNumber',
        VENDOR_ORDER_NUMBER: 'vendorOrderNumber',
        ORDER_RETURN_ID: 'orderReturnId',
    };
    const addContainsLoopbackFilter = key => {
        filterGroups[key].or.push(
            ...searchFilters[key].map(filter => {
                return createContainsLoopbackFilter(
                    searchFilterProperties[key],
                    filter.term
                ).where;
            })
        );
    };
    Object.keys(searchFilters).forEach(key => {
        if (!filterGroups[key]) {
            filterGroups[key] = { or: [] };
        }

        if (Object.keys(searchFilterProperties).includes(key)) {
            addContainsLoopbackFilter(key);
        } else {
            filterGroups[key].or.push({
                jobId: {
                    inq: searchFilters[key].flatMap(filter => filter.term),
                },
            });
        }
    });

    Object.values(filterGroups).forEach(group => {
        if (group.or.length > 0) {
            query.where.and.push(group);
        }
    });

    // Advanced filter section
    const priorityFilter = { or: [] };
    const fulfillmentFilter = { or: [] };
    const statusFilter = { or: [] };
    const tradeFilter = { or: [] };
    const vendorFilter = { or: [] };
    const logisticTypes = { or: [] };
    const returnStatusFilter = { or: [] };
    const issueFilter = { or: [] };
    const orderTypeFilter = { or: [] };
    const segmentsFilter = [];
    const lineBusinessFilter = [];
    const financialStatuses = [];
    const issueForms = [];
    const issueReasonForms = [];
    let salesInvoice = '';
    let purchaseInvoice = '';

    Object.keys(advancedFilters).forEach(key => {
        switch (key) {
            case 'priority':
                priorityFilter.or.push({
                    priorityId: {
                        inq: advancedFilters[key].map(item => item.term),
                    },
                });
                break;
            case 'fulfillment':
                fulfillmentFilter.or.push({
                    fulfillmentTypeId: {
                        inq: advancedFilters[key].map(item => item.term),
                    },
                });
                break;
            case 'status':
                statusFilter.or.push({
                    statusId: {
                        inq: advancedFilters[key].map(item => item.term),
                    },
                });
                break;
            case 'issue':
                advancedFilters[key].forEach(filter => {
                    if (filter.term === 'NONE') {
                        issueFilter.or.push({
                            issueIdSearch: 'NONE',
                        });
                    } else if (filter.term === 'SUBMITTED_APPROVAL_REQUIRED') {
                        issueFilter.or.push({
                            issueIdSearch: { ilike: `%APPROVAL_REQUIRED%` },
                        });
                    } else if (filter.term === 'SUBMITTED_APPROVAL_DENIED') {
                        issueFilter.or.push({
                            clientOrderApprovalStatus: 'DENIED',
                        });
                    } else {
                        issueFilter.or.push({
                            issueId: { ilike: `%"${filter.term}"%` },
                        });
                        issueForms.push(filter.form);
                    }
                });
                break;
            case 'issueReasonIds':
                issueReasonForms.push(
                    ...advancedFilters[key].map(filter => filter.form)
                );
                break;
            case 'returnStatus':
                advancedFilters[key].forEach(item => {
                    if (item.term) {
                        returnStatusFilter.or.push({
                            orderReturnStatusId: {
                                ilike: `%#${item.term}#%`,
                            },
                        });
                    }
                });
                break;
            case 'trade':
                tradeFilter.or.push({
                    tradeTypeId: {
                        inq: advancedFilters[key].map(item => item.term),
                    },
                });
                break;
            case 'segments':
                segmentsFilter.push(
                    ...advancedFilters[key].map(filter => filter.form)
                );
                break;
            case 'lineOfBusiness':
                lineBusinessFilter.push(
                    ...advancedFilters[key].map(filter => filter.form)
                );
                break;
            case 'orderedBefore':
                query.where.and.push({
                    orderedOn: {
                        lt: moment(advancedFilters[key].term)
                            .endOf('day')
                            .subtract(1, 'days'),
                    },
                });
                break;
            case 'orderedAfter':
                query.where.and.push({
                    orderedOn: {
                        gt: moment(advancedFilters[key].term)
                            .endOf('day')
                            .subtract(1, 'days'),
                    },
                });
                break;
            case 'created90Days':
                query.where.and.push({
                    created: {
                        gt: moment().subtract(90, 'days'),
                        lt: moment().add(1, 'days'),
                    },
                });
                break;
            case 'createdBefore':
                query.where.and.push({
                    created: {
                        lt: moment(advancedFilters[key].term)
                            .endOf('day')
                            .subtract(1, 'days'),
                    },
                });
                break;
            case 'createdAfter':
                query.where.and.push({
                    created: {
                        gt: moment(advancedFilters[key].term)
                            .endOf('day')
                            .subtract(1, 'days'),
                    },
                });
                break;
            case 'ownerIs':
                if (advancedFilters[key].term === 'Just Me') {
                    query.where.and.push({ userId });
                }
                break;
            case 'vendor':
                vendorFilter.or.push({
                    fulfillmentDistributionCenterId: {
                        inq: advancedFilters[key].flatMap(
                            filter => filter.term
                        ),
                    },
                });
                break;
            case 'financialStatus':
                financialStatuses.push(
                    ...advancedFilters[key].map(filter => filter.term)
                );
                break;
            case 'JOB_NUMBER':
                query.where.and.push({
                    jobId: {
                        inq: advancedFilters[key].flatMap(
                            filter => filter.term
                        ),
                    },
                });
                break;
            case 'logistic':
                logisticTypes.or.push({
                    'fulfillmentLogistic.type': {
                        inq: advancedFilters[key].map(item => item.term),
                    },
                });
                break;
            case 'availableBefore':
                query.where.and.push({
                    or: [
                        {
                            and: [
                                {
                                    'fulfillmentLogistic.expectedAvailabilityEnd':
                                        null,
                                },
                                {
                                    'fulfillmentLogistic.expectedAvailabilityStart':
                                        {
                                            lt: moment(
                                                advancedFilters[key].term
                                            )
                                                .endOf('day')
                                                .subtract(1, 'days'),
                                        },
                                },
                            ],
                        },
                        {
                            and: [
                                {
                                    'fulfillmentLogistic.expectedAvailabilityEnd':
                                        {
                                            neq: null,
                                        },
                                },
                                {
                                    'fulfillmentLogistic.expectedAvailabilityEnd':
                                        {
                                            lt: moment(
                                                advancedFilters[key].term
                                            )
                                                .endOf('day')
                                                .subtract(1, 'days'),
                                        },
                                },
                            ],
                        },
                    ],
                });
                break;
            case 'availableAfter':
                query.where.and.push({
                    or: [
                        {
                            and: [
                                {
                                    'fulfillmentLogistic.expectedAvailabilityEnd':
                                        null,
                                },
                                {
                                    'fulfillmentLogistic.expectedAvailabilityStart':
                                        {
                                            gt: moment(
                                                advancedFilters[key].term
                                            )
                                                .endOf('day')
                                                .subtract(1, 'days'),
                                        },
                                },
                            ],
                        },
                        {
                            and: [
                                {
                                    'fulfillmentLogistic.expectedAvailabilityEnd':
                                        {
                                            neq: null,
                                        },
                                },
                                {
                                    'fulfillmentLogistic.expectedAvailabilityEnd':
                                        {
                                            gt: moment(
                                                advancedFilters[key].term
                                            )
                                                .endOf('day')
                                                .subtract(1, 'days'),
                                        },
                                },
                            ],
                        },
                    ],
                });
                break;
            case 'planned':
                if (advancedFilters[key].term) {
                    query.where.and.push({ projectId: { neq: null } });
                } else {
                    query.where.and.push({ projectId: null });
                }
                break;
            case 'vendorOrderNumberExists':
                query.where.and.push(
                    advancedFilters[key].term === 'Exists'
                        ? { vendorOrderNumber: { neq: null } }
                        : { vendorOrderNumber: null }
                );
                break;
            case 'vendorVerified':
                query.where.and.push({
                    vendorVerifiedAutomated:
                        advancedFilters[key].term === 'Checked',
                });

                break;
            case 'localTruckCheckBoxAutomated':
                query.where.and.push({
                    vendorLocalTruckAutomated:
                        advancedFilters[key].term === 'Checked',
                });
                break;
            case 'manualTrackingNumber':
                query.where.and.push(
                    advancedFilters[key].term === 'Populated'
                        ? {
                              vendorShipTrackingNumberManual: { neq: null },
                          }
                        : {
                              vendorShipTrackingNumberManual: null,
                          }
                );
                break;
            case 'localTruckCheckBoxManual':
                query.where.and.push({
                    vendorLocalTruckManual:
                        advancedFilters[key].term === 'Checked',
                });
                break;
            case 'directSupply':
                query.where.and.push(
                    { contractorId: { neq: null } },
                    { documentSubTypeId: { neq: ORDER_TYPE.LABOR_WARRANTY } }
                );
                break;
            case 'eComm':
                query.where.and.push({ isEcommOrder: true });
                break;
            case 'ecommDirectSupply':
                query.where.and.push({ isEcommOrder: { neq: false } });
                break;
            case 'salesInvoiceNumber':
                salesInvoice = advancedFilters[key].term.trim();
                break;
            case 'purchaseInvoiceNumber':
                purchaseInvoice = advancedFilters[key].term.trim();
                break;
            case 'integrated':
                query.where.and.push({
                    documentSubTypeId: 'ORDER_WITH_ORIGIN',
                });
                break;
            case 'equipmentOnly':
                query.where.and.push({ documentSubTypeId: 'ORDER' });
                break;
            case 'orderType':
                orderTypeFilter.or.push({
                    fulfillmentMethod: {
                        inq: advancedFilters[key].map(item => item.term),
                    },
                });
                break;
            case 'country':
                query.where.and.push({ country: advancedFilters[key].term });
                break;
            case 'laborWarranty':
                query.where.and.push({
                    documentSubTypeId: ORDER_TYPE.LABOR_WARRANTY,
                });
                break;
            case 'hasEquipmentWarrantyAssets':
                query.where.and.push({
                    hasEquipmentWarrantyAssets: true,
                });
                break;
            default:
        }
    });

    let billingList;
    let purchaseList;
    if (salesInvoice && !purchaseInvoice) {
        billingList = await getOrderIdByBillingStatues(
            financialStatuses,
            salesInvoice
        );
        purchaseList = await getOrderIdByPaymentStatues(
            financialStatuses,
            purchaseInvoice,
            billingList.length > 0 ? billingList : undefined
        );
    } else if (purchaseInvoice && !salesInvoice) {
        purchaseList = await getOrderIdByPaymentStatues(
            financialStatuses,
            purchaseInvoice
        );
        billingList = await getOrderIdByBillingStatues(
            financialStatuses,
            salesInvoice,
            purchaseList.length > 0 ? purchaseList : undefined
        );
    } else {
        [billingList, purchaseList] = await Promise.all([
            getOrderIdByBillingStatues(financialStatuses, salesInvoice),
            getOrderIdByPaymentStatues(financialStatuses, purchaseInvoice),
        ]);
    }

    if (!salesInvoice && !purchaseInvoice && financialStatuses.length) {
        query.where.and.push({
            id: { inq: uniq(concat(billingList, purchaseList)) },
        });
    } else {
        if (salesInvoice || financialStatuses.length) {
            query.where.and.push({ id: { inq: billingList } });
        }
        if (purchaseInvoice || financialStatuses.length) {
            query.where.and.push({ id: { inq: purchaseList } });
        }
    }

    if (issueReasonForms.length > 0) {
        const _orderIds = await getOrderIdByIssueReasonIds(
            issueForms,
            issueReasonForms
        );
        query.where.and.push({ id: { inq: _orderIds.map(o => o.orderId) } });
    }

    query.where.and.push(
        priorityFilter,
        fulfillmentFilter,
        statusFilter,
        issueFilter,
        tradeFilter,
        vendorFilter,
        logisticTypes,
        returnStatusFilter,
        orderTypeFilter
    );

    if (segmentsFilter.length > 0 || lineBusinessFilter.length > 0) {
        let clientIdArray = [];
        const retailStoreInfo = [];

        const retailStoreInfoFilters = [
            'retailRegion',
            'retailStore',
            'retailStoreNumber',
        ];

        retailStoreInfoFilters.forEach(filter => {
            if (advancedFilters[filter]) {
                retailStoreInfo.push({
                    target: filter,
                    term: advancedFilters[filter].term,
                });
            }
        });

        clientIdArray = union(
            clientIdArray,
            await AdminClientsService.getClientIdBySettingsType(
                segmentsFilter,
                lineBusinessFilter,
                retailStoreInfo
            )
        );
        const clientIdFilter = { clientId: { inq: clientIdArray } };
        query.where.and.push({ or: [clientIdFilter] });
    }

    return query;
}

async function getOrderIdByBillingStatues(statuses, invoiceNumber, orderIds) {
    if (statuses.length > 0 || invoiceNumber) {
        return FinancialService.findOrderBillingByStatuses(
            statuses,
            invoiceNumber,
            orderIds
        ).then(billings => billings.map(b => b.orderId));
    }
    return [];
}

async function getOrderIdByPaymentStatues(statuses, invoiceNumber, orderIds) {
    if (statuses.length > 0 || invoiceNumber) {
        return FinancialService.findOrderPaymentByStatuses(
            statuses,
            invoiceNumber,
            orderIds
        ).then(billings => billings.map(b => b.orderId));
    }
    return [];
}

async function getOrderIdByIssueReasonIds(
    issueForms = [],
    issueReasonForms = []
) {
    if (issueForms.length > 0 && issueReasonForms.length > 0) {
        const _conditions = map(issueForms, issueIdForm => {
            const _condition = {
                and: [{ issueId: issueIdForm.id }, { active: true }],
            };
            const relateReasonIds = map(issueIdForm.reasons, res => res.id);
            const relateReasonForms = _filter(
                issueReasonForms,
                issueReasonForm => relateReasonIds.includes(issueReasonForm.id)
            );
            if (relateReasonForms && relateReasonForms.length > 0) {
                const _reasonClause = {
                    or: [],
                };
                relateReasonForms.forEach(relReaForm => {
                    _reasonClause.or.push({
                        issueReasonId: { like: `%"${relReaForm.id}"%` },
                    });
                });
                _condition.and.push(_reasonClause);
            }

            return _condition;
        });

        const filter = {
            fields: ['orderId'],
            where: { and: [{ or: _conditions }] },
        };

        return ApiService.get(true, `OrderIssueTrackings`, { filter });
    }
    return [];
}

export function removeLineItem(orderId, lineItemId) {
    return OrderService.deleteLineItem(orderId, lineItemId).then(() =>
        getOrder(orderId)
    );
}

export function addLineItems(orderId, products) {
    // products is of form {product, supplied}
    const createLineItems = products.map(p => {
        const _p = {
            sku: p.product.sku,
            suppliedQuantity: p.supplied.quantity,
            suppliedDescription: p.supplied.description,
            suppliedPayout: p.supplied.suppliedPayout,
            suppliedVendorCost: p.supplied.suppliedVendorCost,
            img: p.product.img,
            details: p.product.details,
        };
        return OrderService.createLineItem(orderId, _p);
    });
    return Promise.all(createLineItems);
}

/**
 * update a line item
 * @param {number} id
 * @param {number} lineItemId
 * @param {object} lineItem
 * @param {boolean} [finance]
 * @returns {Promise<Object>}
 */
export function updateLineItem(id, lineItemId, lineItem, finance) {
    return OrderService.updateLineItem(id, lineItemId, lineItem, finance);
}

/**
 * update line item serial numbers
 * @param {number} id
 * @param {object} serialNumberData
 * @returns {Promise<Object>}
 */
export function updateOrderSerialNumbers(id, serialNumberData) {
    return OrderService.updateOrderSerialNumbers(id, serialNumberData);
}

/**
 * adjust a line item
 * @param {number} id
 * @param {number} lineItemId
 * @param {object} adjustment
 * @param {boolean} [finance]
 * @returns {Promise}
 */
export function adjustFinanceLineItem(id, lineItemId, adjustment, finance) {
    return OrderService.adjustFinanceLineItem(
        id,
        lineItemId,
        adjustment,
        finance
    );
}

/**
 * bulk adjust line items
 * @param {number} orderId
 * @param {array} bulkAdjustment
 * @param {object} adjustmentReason
 * @param {string} adjustmentReasonNote
 * @returns {Promise}
 */
export function bulkAdjustLineItems(
    orderId,
    bulkAdjustment,
    adjustmentReason,
    adjustmentReasonNote
) {
    return OrderService.bulkAdjustLineItems(
        orderId,
        bulkAdjustment,
        adjustmentReason,
        adjustmentReasonNote
    );
}

/**
 * approve a line item
 * @param {Number} id
 * @param {Number} lineItemId
 * @param {String} reason
 * @returns {Promise}
 */
export function approveLineItem(id, lineItemId, reason) {
    return OrderService.approveLineItem(id, lineItemId, reason);
}

export function substituteLineItem(id, lineItemId, newLineItem, isCustom) {
    return OrderService.substituteLineItem(
        id,
        lineItemId,
        newLineItem,
        isCustom
    );
}

export function changeLineItem(id, lineItemId, newLineItem, isCustom) {
    return OrderService.changeLineItem(id, lineItemId, newLineItem, isCustom);
}

export function createNote(id, data, addToComment, editable = true) {
    const _note = {
        ...data,
    };
    return OrderService.createNote(id, _note, addToComment, editable);
}

export function updateNote(id, note, mentionsData) {
    const _note = {
        message: note.message,
        pinned: note.pinned,
        mentionsData,
    };
    return OrderService.updateNote(id, note.id, _note);
}

export function createComment(id, comment, mentionsData) {
    const _comment = {
        message: comment,
        messageType: 'MOTILI',
        mentionsData,
    };
    return OrderService.createComment(id, _comment);
}

export function updateComment(id, comment, mentionsData) {
    const _comment = {
        message: comment.message,
        mentionsData,
    };
    return OrderService.updateComment(id, comment.id, _comment);
}

export function addLogistic(id, logistic) {
    return ApiService.post(true, `Orders/${id}/addLogistic`, logistic);
}

export function removeLogistic(id) {
    return ApiService.post(true, `Orders/${id}/removeLogistic`);
}

/**
 * @param  {number} orderId            {order id}
 * @param  {object} fulfillmentDetails {fulfillment details}
 * @return {Promise} {OrderService.update}
 */
export function updateFulfillmentDetails(orderId, fulfillmentDetails) {
    const update = {
        fulfillmentDetails: OrderService.mapFulfillmentDetailsFromTruck(
            {},
            fulfillmentDetails
        ),
    };

    return OrderService.update(orderId, update);
}

export function create(_order) {
    return OrderService.create(_order);
}

export function addContact(orderId, contact) {
    return OrderService.createContact(orderId, contact);
}

export function removeContact(orderId, contactId) {
    return OrderService.removeContact(orderId, contactId);
}

export function patch(orderId, update) {
    return OrderService.patch(orderId, update);
}

export function order(orderId, adjustedFreight, advancedFulfill = false) {
    return OrderService.order(
        orderId,
        false,
        adjustedFreight,
        true,
        advancedFulfill
    );
}

export function reOrder(orderId, adjustedFreight) {
    return OrderService.reOrder(orderId, adjustedFreight, true);
}

export function submit(orderId) {
    return OrderService.submit(orderId, true);
}

export function complete(orderId, reason) {
    return OrderService.complete(orderId, reason);
}

export function close(orderId) {
    return OrderService.close(orderId);
}

export function cancelAndClone(orderId, reason, reasonId, secondaryReasonId) {
    return OrderService.cancelAndClone(
        orderId,
        reason,
        reasonId,
        secondaryReasonId
    );
}

export function clone(orderId, reason) {
    return OrderService.clone(orderId, reason);
}

export function changePriority(
    orderId,
    priorityId,
    reasonId,
    reasonIdLabel,
    reason
) {
    return OrderService.changePriority(
        orderId,
        priorityId,
        reasonId,
        reasonIdLabel,
        reason
    );
}

export function placeHold(orderId, options, status) {
    return status.toUpperCase() === 'SUBMITTED'
        ? OrderService.placeHoldSubmitted(orderId, options, false)
        : OrderService.placeHold(orderId, options, false);
}

export function generatePdf(
    id,
    fileName,
    statementOfWork,
    selectedLineItemIds,
    hidePricing,
    hideBundleItems,
    hideSerialNumbers
) {
    return OrderService.generatePdf(
        id,
        fileName,
        statementOfWork,
        selectedLineItemIds,
        hidePricing,
        hideBundleItems,
        hideSerialNumbers
    );
}

export function exportCsv(filter) {
    return OrderService.exportCsv(filter);
}

export function restoreOrder(orderId, reason) {
    return OrderService.restore(orderId, reason);
}

export function cancel(
    orderId,
    reason,
    reasonId,
    secondaryReasonId,
    vendorSystemConfirmationNumber
) {
    return OrderService.cancel(
        orderId,
        reason,
        reasonId,
        secondaryReasonId,
        vendorSystemConfirmationNumber,
        true
    );
}

export function mapFulfillmentDetailsFromTruck(_order, fulfillmentDetails) {
    return OrderService.mapFulfillmentDetailsFromTruck(
        _order,
        fulfillmentDetails
    );
}

export function confirm(orderId, optionalOrderObject) {
    return OrderService.confirm(orderId, optionalOrderObject);
}

export function findFreightRate(orderId) {
    return OrderService.findFreightRate(orderId);
}

/**
 * retrieves all billings and payments of the work order
 * @param {number} id
 * @returns {Promise<Array>}
 */
export function fetchFinances(id) {
    return ApiService.get(true, `orders/${id}/finances`);
}

/**
 * retrieves all auto invoice history of the order
 * @param {array} invoiceNumbers
 * @returns {Promise<Array>}
 */
export function fetchAutoInvoiceHistory(invoiceNumbers) {
    return ApiService.post(
        true,
        'orders/finances/autoInvoiceHistory',
        invoiceNumbers
    );
}

/**
 * trigger auto invoice
 * @param {number} clientId
 * @param {number} autoInvoiceNotificationHistoryId
 * @returns {Promise<Object>}
 */
export function triggerAutoInvoice(clientId, historyId) {
    return ApiService.get(
        true,
        `orders/finances/${clientId}/triggerAutoInvoice/${historyId}`
    );
}

/**
 * update billing of the order
 * @param {number} orderId
 * @param {number} billingId
 * @param {object} billingData
 * @returns {Promise<Object>}
 */
export function billed(orderId, billingId, billingData) {
    return ApiService.post(true, `Orders/${orderId}/billed/`, billingData, {
        billingId,
    });
}

/**
 * update payment of the order
 * @param {number} orderId
 * @param {number} paymentId
 * @param {object} paymentData
 * @returns {Promise<Object>}
 */
export function paid(orderId, paymentId, paymentData) {
    return ApiService.post(true, `Orders/${orderId}/paid/`, paymentData, {
        paymentId,
    });
}

export function syncPayment(paymentId) {
    return ApiService.get(true, `Orders/syncPayment/${paymentId}`);
}

export function syncBilling(billingId) {
    return ApiService.get(true, `Orders/syncBilling/${billingId}`);
}

export function forceSync(orderId) {
    return ApiService.get(true, `Orders/${orderId}/forceSync`);
}

export function syncPaymentReturn(paymentId, paymentReturnId) {
    return ApiService.get(
        true,
        `Orders/syncPayment/${paymentId}/paymentReturns/${paymentReturnId}/sync`
    );
}

export function syncBillingReturn(billingId, billingReturnId) {
    return ApiService.get(
        true,
        `Orders/syncBilling/${billingId}/billingReturns/${billingReturnId}/sync`
    );
}

export function fulfill(orderId, options, adjustedFreight) {
    return OrderService.fulfill(orderId, options, adjustedFreight);
}

export function ordered(orderId, options) {
    return OrderService.ordered(orderId, options);
}

export function compare(orderId) {
    return OrderService.compare(orderId);
}

export function shouldDisplayActionExport(documentOrigination) {
    return documentOrigination === null;
}

export function shouldDisplayActionRestore(orderStatusId) {
    return orderStatusId === 'CANCELLING' && isMotiliAdmin();
}

export function shouldDisplayActionOrder(orderStatusId) {
    return orderStatusId === 'CREATED';
}

export function shouldDisplayActionFulfill(
    orderStatusId,
    clientOrderApprovalStatus
) {
    if (
        orderStatusId === 'ORDER_REQUESTED' ||
        clientOrderApprovalStatus === 'APPROVAL_REQUIRED'
    ) {
        return false;
    }
    return orderStatusId === 'SUBMITTED' || orderStatusId === 'HOLD';
}

export function shouldDisplayActionHold(orderStatusId) {
    if (orderStatusId === 'ORDER_REQUESTED') {
        return false;
    }
    return (
        orderStatusId === 'ORDERED' ||
        orderStatusId === 'ORDER_REQUESTED' ||
        orderStatusId === 'SUBMITTED'
    );
}

export function shouldDisplayActionCancelAndClone(orderStatusId) {
    return orderStatusId === 'ORDERED' || orderStatusId === 'ORDER_REQUESTED';
}

export function shouldDisplayActionMoveToOrdered(orderStatusId) {
    return orderStatusId === 'ORDER_REQUESTED';
}

export function shouldDisplayActionComplete(orderStatusId) {
    const acceptableRoles = [
        'motili_owner',
        'motili_admin',
        'motili_manager',
        'motili_user',
    ];
    return orderStatusId === 'ORDERED' && hasRole(acceptableRoles);
}

export function shouldDisplayActionChangePriority(orderStatusId) {
    if (!orderStatusId) {
        return false;
    }
    const acceptableStatuses = [
        'CREATED',
        'SUBMITTED',
        'HOLD',
        'ORDERED',
        'ORDERED_CONFIRM',
    ];
    return !!(
        isMotiliAccount() && acceptableStatuses.indexOf(orderStatusId) !== -1
    );
}

export function shouldDisplayActionTransferOwner(orderStatusId) {
    if (!orderStatusId) {
        return false;
    }
    const acceptableStatuses = [
        'CREATED',
        'SUBMITTED',
        'HOLD',
        'ORDERED',
        'ORDERED_CONFIRM',
    ];
    return !!(
        isMotiliAccount() && acceptableStatuses.indexOf(orderStatusId) !== -1
    );
}

export function shouldDisplayVendorActivity() {
    return !!isMotiliAccount();
}

export function shouldDisplayNotifications() {
    return !!isMotiliAccount();
}

export function shouldDisplayActionModify(orderStatusId) {
    return orderStatusId === 'ORDERED_CONFIRM';
}

export function shouldDisplayActionCancel(orderStatusId) {
    return (
        orderStatusId !== 'CANCELLED' &&
        orderStatusId !== 'CLOSED' &&
        orderStatusId !== 'COMPLETED'
    );
}

export function shouldDisplayActionClose(orderStatusId) {
    return (
        orderStatusId === 'COMPLETED' &&
        (isMotiliAdmin() || isMotiliFinanceDept())
    );
}

export function shouldDisplayActionReOrder(
    orderStatusId,
    clientOrderApprovalStatus
) {
    if (clientOrderApprovalStatus === 'APPROVAL_REQUIRED') {
        return false;
    }
    return orderStatusId === 'SUBMITTED';
}

export function shouldDisplayCreateReturn(order) {
    const orderStatusId = order.statusId;
    const activeReturn =
        order.orderReturns &&
        order.orderReturns.find(
            re =>
                re.statusId === 'REQUESTED' ||
                re.statusId === 'ACTIVE' ||
                re.statusId === 'DENIED' ||
                re.statusId === 'APPROVED' ||
                re.statusId === 'PARTIAL_APPROVED' ||
                re.statusId === 'COMPLETED'
        );
    return (
        !activeReturn &&
        haveItemsToReturn(order) &&
        (orderStatusId === 'CLOSED' || orderStatusId === 'COMPLETED') &&
        isMotiliAccount()
    );
}

export function shouldDisplayEditReturn(activeReturn) {
    if (activeReturn) {
        if (
            activeReturn.statusId === 'REQUESTED' ||
            activeReturn.statusId === 'ACTIVE' ||
            activeReturn.statusId === 'DENIED' ||
            activeReturn.statusId === 'PARTIAL_APPROVED' ||
            activeReturn.statusId === 'APPROVED'
        ) {
            return isMotiliAccount();
        }
        if (activeReturn.statusId === 'COMPLETED') {
            return isMotiliEngineeringDept() || isMotiliFinanceDept();
        }
    }
    return false;
}

export function shouldDisplayCancelReturn(activeReturn) {
    return shouldDisplayEditReturn(activeReturn);
}
export function shouldDisplayDenyReturn(activeReturn) {
    if (activeReturn) {
        if (
            activeReturn.statusId === 'ACTIVE' ||
            activeReturn.statusId === 'APPROVED' ||
            activeReturn.statusId === 'PARTIAL_APPROVED'
        ) {
            return isMotiliAccount();
        }
    }
    return false;
}

export function shouldDisplayPartialApproveReturn(activeReturn) {
    if (activeReturn) {
        if (
            activeReturn.statusId === 'ACTIVE' ||
            activeReturn.statusId === 'DENIED' ||
            activeReturn.statusId === 'APPROVED'
        ) {
            return isMotiliAccount();
        }
    }
    return false;
}

export function shouldDisplayApproveReturn(activeReturn) {
    if (activeReturn) {
        if (
            activeReturn.statusId === 'ACTIVE' ||
            activeReturn.statusId === 'DENIED' ||
            activeReturn.statusId === 'PARTIAL_APPROVED'
        ) {
            return isMotiliAccount();
        }
    }
    return false;
}

export function shouldDisplayActivateReturn(activeReturn) {
    if (activeReturn) {
        if (isMotiliAccount()) {
            return activeReturn.statusId === 'REQUESTED';
        }
    }
    return false;
}

export function shouldDisplayCompleteReturn(activeReturn) {
    if (activeReturn) {
        if (isMotiliEngineeringDept() || isMotiliFinanceDept()) {
            return (
                activeReturn.statusId === 'DENIED' ||
                activeReturn.statusId === 'PARTIAL_APPROVED' ||
                activeReturn.statusId === 'APPROVED'
            );
        }
    }
    return false;
}

export function shouldDisplayCloseReturn(activeReturn) {
    if (activeReturn) {
        if (isMotiliEngineeringDept() || isMotiliFinanceDept()) {
            return activeReturn.statusId === 'COMPLETED';
        }
    }
    return false;
}

// eslint-disable-next-line no-shadow
export function shouldDisplayActionClone(order) {
    const _sequence = order?.job?.sequence || [];
    const maxSeq = Math.max(..._sequence.map(seq => seq.sequenceNumber));
    const lastDocument = _sequence.find(seq => seq.sequenceNumber === maxSeq);
    const orderEventClone = order?.events?.find(
        event =>
            event.orderId === order.id &&
            event.actionOptions &&
            event.actionOptions.clonedTo &&
            (event.action === 'CANCEL_AND_CLONE' || event.action === 'CLONE')
    );

    return (
        isMotiliAccount() &&
        lastDocument &&
        order?.id === lastDocument?.documentId &&
        order?.statusId === 'CANCELLED' &&
        order?.orderTypeId === 'ORDER' &&
        !orderEventClone
    );
}

/**
 * determines if line item action remove is available
 * @param {Object} order
 * @param {String} lineItemStatusId
 * @param {any[]} bundleLineItems
 * @returns {Boolean}
 */
export function shouldDisplayLineItemActionRemove(
    order,
    lineItemStatusId,
    bundleLineItems = []
) {
    if (!order || !order.statusId || !lineItemStatusId) {
        return false;
    }

    const acceptableOrderStatuses = ['CREATED', 'SUBMITTED', 'ORDER_REQUESTED'];
    const acceptableLineItemStatuses = ['CREATED', 'APPROVED'];
    if (
        (acceptableOrderStatuses.indexOf(order.statusId) !== -1 &&
            isMotiliAccount()) ||
        shouldDisplayOrderEditAPFinance(order)
    ) {
        if (
            acceptableLineItemStatuses.indexOf(lineItemStatusId) !== -1 &&
            (bundleLineItems.length === 0 ||
                !some(
                    bundleLineItems,
                    item => !acceptableLineItemStatuses.includes(item.statusId)
                ))
        ) {
            return true;
        }
    }
    return false;
}

/**
 * determines if line item action approve is available
 * @param {String} orderStatusId
 * @param {String} lineItemStatusId
 * @returns {Boolean}
 */
export function shouldDisplayLineItemActionApprove(
    orderStatusId,
    lineItemStatusId,
    bundleJobLineItemId
) {
    if (!orderStatusId || !lineItemStatusId || bundleJobLineItemId) {
        return false;
    }

    const acceptableOrderStatuses = ['CREATED', 'SUBMITTED'];
    if (
        acceptableOrderStatuses.indexOf(orderStatusId) !== -1 &&
        isMotiliAccount()
    ) {
        if (lineItemStatusId === 'CREATED') {
            return true;
        }
    }
    return false;
}

/**
 * determines if line item action edit is available
 * @param {Object} order
 * @param {Object} lineItem
 * @returns {Boolean}
 */
export function shouldDisplayLineItemActionEdit(order, lineItem) {
    if (!order || !order.statusId || !lineItem) {
        return false;
    }

    const acceptableOrderStatuses = ['CREATED', 'SUBMITTED', 'ORDER_REQUESTED'];
    const acceptableLineItemStatuses = ['CREATED', 'APPROVED'];
    const acceptableRoles = ['motili_owner', 'motili_admin'];
    return (
        ((acceptableOrderStatuses.includes(order.statusId) &&
            hasRole(acceptableRoles)) ||
            shouldDisplayOrderEditAPFinance(order)) &&
        acceptableLineItemStatuses.includes(lineItem.statusId)
    );
}

/**
 * determines if line item can edit quantity
 * @param {array} lineItems
 * @param {object} lineItem
 * @returns {Boolean}
 */
export function shouldAllowEditLineItemQuantity(lineItems, lineItem) {
    if (
        lineItem.productCategoryId === 'BUNDLE' &&
        lineItems
            .filter(li => li.bundleJobLineItemId === lineItem.id)
            .some(li =>
                [
                    'COMPLETED',
                    'PARTIALLY_RETURNED',
                    'RETURNED',
                    'RETURNING',
                ].includes(li.statusId)
            )
    ) {
        return false;
    }
    return !lineItem.bundleJobLineItemId;
}

/**
 * determines if line item action edit is available
 * @param {String} orderStatusId
 * @param {Object} lineItem
 * @returns {Boolean}
 */
export function shouldDisplayLineItemActionEditSerialNumber(
    orderStatusId,
    lineItem
) {
    if (!orderStatusId || !lineItem) {
        return false;
    }

    const acceptableRoles = [
        'motili_owner',
        'motili_admin',
        'motili_manager',
        'motili_user',
    ];
    const acceptableLineItemStatuses = ['CREATED', 'APPROVED', 'COMPLETED'];
    return (
        hasValidSerialNumberEditOrderStatus(orderStatusId) &&
        acceptableLineItemStatuses.includes(lineItem.statusId) &&
        hasValidSerialNumberEditCategory(lineItem) &&
        hasRole(acceptableRoles)
    );
}

/**
 * determines if line item action substitute is available
 * @param {Object} order
 * @param {String} lineItemStatusId
 * @param {Object} lineItem
 * @returns {Boolean}
 */
export function shouldDisplayLineItemActionSubstitute(
    order,
    lineItemStatusId,
    lineItem
) {
    if (!order || !order.statusId || !lineItemStatusId || !lineItem) {
        return false;
    }

    const acceptableOrderStatuses = ['CREATED', 'SUBMITTED', 'ORDER_REQUESTED'];
    const acceptableProductCategoryIds = ['EQUIPMENT', 'PART', 'LABOR'];
    const acceptableCustomProductSubCategoryIds = ['EQUIPMENT', 'PART'];
    if (
        (acceptableOrderStatuses.indexOf(order.statusId) !== -1 &&
            isMotiliAccount()) ||
        shouldDisplayOrderEditAPFinance(order)
    ) {
        if (lineItemStatusId === 'APPROVED') {
            if (
                acceptableProductCategoryIds.indexOf(
                    lineItem.productCategoryId
                ) !== -1
            ) {
                return true;
            }
            if (
                lineItem.productCategoryId === 'CUSTOM' &&
                acceptableCustomProductSubCategoryIds.indexOf(
                    lineItem.productSubCategoryId
                ) !== -1
            ) {
                return true;
            }
        }
    }
    return false;
}

/**
 * determines if line item action change is available
 * @param {Object} order
 * @param {String} lineItemStatusId
 * @param {Object} lineItem
 * @returns {Boolean}
 */
export function shouldDisplayLineItemActionChange(
    order,
    lineItemStatusId,
    lineItem
) {
    if (!order || !order.statusId) {
        return false;
    }
    if (lineItem.bundleJobLineItemId) {
        return false;
    }

    const acceptableOrderStatuses = ['CREATED', 'SUBMITTED', 'ORDER_REQUESTED'];
    const acceptableProductCategoryIds = ['EQUIPMENT', 'PART'];
    if (
        (acceptableOrderStatuses.indexOf(order.statusId) !== -1 &&
            isMotiliAccount()) ||
        shouldDisplayOrderEditAPFinance(order)
    ) {
        if (lineItemStatusId === 'APPROVED') {
            if (
                acceptableProductCategoryIds.indexOf(
                    lineItem.productCategoryId
                ) !== -1
            ) {
                return true;
            }
            if (
                lineItem.productCategoryId === 'CUSTOM' &&
                acceptableProductCategoryIds.indexOf(
                    lineItem.productSubCategoryId
                ) !== -1
            ) {
                return true;
            }
        }
    }
    return false;
}

export function updateReferenceNumber(orderId, referenceToUpdate) {
    if (!orderId) return Promise.reject(new Error('Order Id is required'));
    return OrderService.updateReferenceNumber(orderId, referenceToUpdate);
}

export function getDocumentByJobNumber(jobNumber) {
    if (!jobNumber) return Promise.resolve([]);
    return DocumentService.getDocumentByJobNumber('Orders', jobNumber, {
        fields: ['id', 'jobId'],
    });
}

export function getPriorityTypes() {
    return ApiService.get(true, 'Orders/priorities').then(res =>
        res.map(item => ({ display: item.display, id: item.id }))
    );
}

export function getPriorityChangeReasonTypes() {
    return ApiService.get(true, 'Orders/priorityChangeReasons').then(res =>
        res.map(item => ({ display: item.display, id: item.id }))
    );
}

export function shouldDisplayManageAction(statusId) {
    if (!statusId) {
        return false;
    }
    if (statusId === 'ORDER_REQUESTED') {
        return false;
    }
    const acceptableStatuses = [
        'CREATED',
        'SUBMITTED',
        'HOLD',
        'ORDERED',
        'ORDERED_CONFIRM',
    ];
    return !!(isMotiliAccount() && acceptableStatuses.indexOf(statusId) !== -1);
}

export function shouldDisplayActionChangeProject(statusId) {
    if (!statusId) {
        return false;
    }
    const acceptableStatuses = [
        'CREATED',
        'SUBMITTED',
        'HOLD',
        'ORDERED',
        'ORDERED_CONFIRM',
    ];
    return !!(isMotiliAccount() && acceptableStatuses.indexOf(statusId) !== -1);
}

export function shouldDisplaySubActionFlagNone(order) {
    const { issueId } = order;
    return !!(issueId && issueId !== 'NONE');
}

export function shouldDisplayActionFlagApproved(
    statusId,
    clientOrderApprovalStatus
) {
    if (!statusId || !clientOrderApprovalStatus) {
        return false;
    }
    const acceptableStatuses = ['SUBMITTED'];
    return !!(
        isMotiliAccount() &&
        acceptableStatuses.indexOf(statusId) !== -1 &&
        clientOrderApprovalStatus === 'APPROVAL_REQUIRED'
    );
}

export function shouldDisplayActionFlagDenied(
    statusId,
    clientOrderApprovalStatus
) {
    if (!statusId || !clientOrderApprovalStatus) {
        return false;
    }
    const acceptableStatuses = ['SUBMITTED'];
    return !!(
        isMotiliAccount() &&
        acceptableStatuses.indexOf(statusId) !== -1 &&
        clientOrderApprovalStatus === 'APPROVAL_REQUIRED'
    );
}

export function shouldDisplayActionFlag(order) {
    const { statusId } = order;
    const unAcceptableStatuses = ['CANCELLED', 'CLOSED'];
    return (
        isMotiliAccount() &&
        statusId &&
        !unAcceptableStatuses.includes(statusId)
    );
}

export function changeProject(id, projectId) {
    return OrderService.changeProject(id, projectId);
}

export function specifyOrderIssue(id, data) {
    return OrderService.specifyOrderIssue(id, data);
}

export function transferOwner(id, ownerId, coOwnerId) {
    return OrderService.transferOwner(id, ownerId, coOwnerId);
}

export function createReturnOrder(orderId, returnOrder) {
    return ApiService.post(true, `Orders/${orderId}/orderReturns`, returnOrder);
}

export function updateReturnOrder(orderId, returnOrderId, returnOrder) {
    return ApiService.patch(
        true,
        `Orders/${orderId}/orderReturns/${returnOrderId}`,
        returnOrder
    );
}

export function updateReturnOrderFile(orderId, returnOrderId, returnOrder) {
    return ApiService.patch(
        true,
        `Orders/${orderId}/orderReturns/${returnOrderId}/file`,
        returnOrder
    );
}

export function getReturnOrder(orderId, returnOrderId) {
    return ApiService.get(
        true,
        `Orders/${orderId}/orderReturns/${returnOrderId}`
    );
}

export function cancelReturnOrder(orderId, returnOrderId, returnOrder) {
    return ApiService.patch(
        true,
        `Orders/${orderId}/orderReturns/${returnOrderId}/cancel`,
        returnOrder
    );
}

export function denyReturnOrder(orderId, returnOrderId, data) {
    return ApiService.patch(
        true,
        `Orders/${orderId}/orderReturns/${returnOrderId}/deny`,
        data
    );
}

export function partialApproveReturnOrder(orderId, returnOrderId, data) {
    return ApiService.patch(
        true,
        `Orders/${orderId}/orderReturns/${returnOrderId}/partialApprove`,
        data
    );
}

export function approveReturnOrder(orderId, returnOrderId, data) {
    return ApiService.patch(
        true,
        `Orders/${orderId}/orderReturns/${returnOrderId}/approve`,
        data
    );
}

export function activateReturnOrder(orderId, returnOrderId, returnOrder) {
    return ApiService.patch(
        true,
        `Orders/${orderId}/orderReturns/${returnOrderId}/activate`,
        returnOrder
    );
}

export function completeReturnOrder(orderId, returnOrderId, returnOrder) {
    return ApiService.patch(
        true,
        `Orders/${orderId}/orderReturns/${returnOrderId}/complete`,
        returnOrder
    );
}

export function closeReturnOrder(orderId, returnOrderId, returnOrder) {
    return ApiService.patch(
        true,
        `Orders/${orderId}/orderReturns/${returnOrderId}/close`,
        returnOrder
    );
}

export function flagClient(orderId, approved, reason) {
    return OrderService.flagClient(orderId, approved, true, reason);
}

export function getFlagApprovedReasonTypes() {
    return ApiService.get(true, 'Orders/flagApprovedReasonTypes');
}

export function getFlagDeniedReasonTypes() {
    return ApiService.get(true, 'Orders/flagDeniedReasonTypes');
}

export async function financeApproveEdit(document) {
    const updateLineItems = await getFinanceUpdateLineItems(document);
    const data = {
        ...updateLineItems,
        adjustments: document.adjustments,
        adjustmentReason: document.adjustmentReason,
        adjustmentReasonNote: document.adjustmentReasonNote,
    };
    return ApiService.post(
        true,
        `Orders/${document.id}/approveFinanceEdit`,
        data
    );
}

export function shouldDisplayActionFinanceEdit(order) {
    if (!order) {
        return false;
    }
    const statusesAllowResync = ['COMPLETED', 'CLOSED'];
    return !!(
        isMotiliApFinanceDept() && statusesAllowResync.includes(order.statusId)
    );
}

export function shouldDisplayOrderEditAPFinance(order) {
    return (
        order &&
        order.statusId === ORDER_STATUS.ORDERED &&
        order.orderTypeId === ORDER_TYPE.ORDER &&
        isMotiliApFinanceDept()
    );
}

export function isAllowResync(id) {
    return ApiService.get(true, `Orders/${id}/isAllowResync`);
}

export function updateBillTo(orderId, billTo) {
    return OrderService.updateBillTo(orderId, billTo);
}

export function updateFulfillmentMethodIfValid(
    orderId,
    orderFulfillmentMethod,
    truckFulfillmentTypeId
) {
    return OrderService.updateFulfillmentMethodIfValid(
        orderId,
        orderFulfillmentMethod,
        truckFulfillmentTypeId
    );
}

export function getLineItemSummary(orderId) {
    return ApiService.get(true, `Orders/${orderId}/lineItemSummary`);
}

export function createLaborWarranty(order, lineItems) {
    const query = {
        order,
        lineItems,
    };

    return ApiService.post(true, 'Orders/laborWarranty/create', {}, query);
}

export function editLaborWarranty(order, lineItems) {
    const data = {
        order,
        lineItems,
    };

    return ApiService.patch(true, 'Orders/laborWarranty/edit', data);
}

export function processLaborWarranty(orderId) {
    return ApiService.post(true, `Orders/laborWarranty/${orderId}/process`, {});
}

export function calculateTax(orderId) {
    return ApiService.post(true, `Orders/${orderId}/calculateTax`);
}

export function calculateTaxWithDocument(orderId, document) {
    return OrderService.calculateTaxWithDocument(orderId, document);
}

export function editOrder(orderId, data) {
    return ApiService.post(true, `Orders/${orderId}/editOrder`, data);
}

/**
 * retrieves all order related job notes
 * @param {number} orderId
 * @returns {Promise<Array>}
 */
export function getOrderRelatedJobNotes(orderId) {
    return OrderService.getOrderRelatedJobNotes(orderId);
}

/**
 * clones an order
 * @param {number} orderId
 * @param {string} reason
 * @param {Object} [updates]
 * @returns {Promise<{}>}
 */
export function cloneOrder(orderId, reason, updates) {
    return OrderService.cloneOrder(orderId, reason, updates);
}
